/**
 * GL Account service layer for business logic and data operations
 * Handles backend integration with proper error handling and offline support
 */
import { GLAccount } from '../types/glAccount';
import { api, ApiError } from './api';

// Local storage key for offline support
const GL_ACCOUNTS_CACHE_KEY = 'finstack_gl_accounts_cache';
const CACHE_TIMESTAMP_KEY = 'finstack_gl_accounts_cache_timestamp';
const LAST_SYNC_TIMESTAMP_KEY = 'finstack_gl_accounts_last_sync';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export interface GLAccountCreateData {
  number: string;
  name: string;
  description: string;
  category: string;
  subcategory: string;
}

export interface GLAccountUpdateData {
  name?: string;
  description?: string;
  category?: string;
  subcategory?: string;
  active?: boolean;
}

/**
 * GL Account service with offline support and caching
 */
export class GLAccountService {
  /**
   * Get cached GL accounts from localStorage
   */
  private getCachedAccounts(): GLAccount[] | null {
    try {
      const cached = localStorage.getItem(GL_ACCOUNTS_CACHE_KEY);
      const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      
      if (!cached || !timestamp) {
        return null;
      }
      
      const cacheAge = Date.now() - parseInt(timestamp);
      if (cacheAge > CACHE_DURATION) {
        // Cache expired
        this.clearCache();
        return null;
      }
      
      const accounts = JSON.parse(cached) as GLAccount[];
      return accounts.map(account => ({
        ...account,
        createdAt: new Date(account.createdAt),
        updatedAt: new Date(account.updatedAt)
      }));
    } catch (error) {
      console.error('Error reading GL accounts cache:', error);
      return null;
    }
  }

  /**
   * Cache GL accounts to localStorage
   */
  private cacheAccounts(accounts: GLAccount[]): void {
    try {
      const serializedAccounts = accounts.map(account => ({
        ...account,
        createdAt: account.createdAt instanceof Date ? account.createdAt.toISOString() : account.createdAt,
        updatedAt: account.updatedAt instanceof Date ? account.updatedAt.toISOString() : account.updatedAt
      }));
      
      localStorage.setItem(GL_ACCOUNTS_CACHE_KEY, JSON.stringify(serializedAccounts));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      localStorage.setItem(LAST_SYNC_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Error caching GL accounts:', error);
    }
  }

  /**
   * Clear GL accounts cache
   */
  private clearCache(): void {
    localStorage.removeItem(GL_ACCOUNTS_CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    localStorage.removeItem(LAST_SYNC_TIMESTAMP_KEY);
  }

  /**
   * Get the last sync timestamp
   */
  getLastSyncTimestamp(): Date | null {
    try {
      const timestamp = localStorage.getItem(LAST_SYNC_TIMESTAMP_KEY);
      return timestamp ? new Date(parseInt(timestamp)) : null;
    } catch (error) {
      console.error('Error reading last sync timestamp:', error);
      return null;
    }
  }

  /**
   * Get all GL accounts with caching support
   */
  async getAll(useCache: boolean = true): Promise<GLAccount[]> {
    // Try cache first if enabled
    if (useCache) {
      const cached = this.getCachedAccounts();
      if (cached) {
        return cached;
      }
    }

    try {
      const accounts = await api.glAccount.getAll();
      
      // Cache the results
      this.cacheAccounts(accounts);
      
      return accounts;
    } catch (error) {
      // If API fails, try to return cached data as fallback
      const cached = this.getCachedAccounts();
      if (cached) {
        console.warn('API failed, using cached GL accounts');
        return cached;
      }
      
      throw error;
    }
  }

  /**
   * Create a new GL account
   */
  async create(accountData: GLAccountCreateData): Promise<GLAccount> {
    // Validate required fields
    if (!accountData.number?.trim()) {
      throw new ApiError('GL account number is required', 'VALIDATION_ERROR');
    }
    if (!accountData.name?.trim()) {
      throw new ApiError('GL account name is required', 'VALIDATION_ERROR');
    }

    try {
      const newAccount = await api.glAccount.create(accountData);
      
      // Update cache optimistically
      const cached = this.getCachedAccounts();
      if (cached) {
        this.cacheAccounts([...cached, newAccount]);
      }
      
      return newAccount;
    } catch (error) {
      if (error instanceof ApiError && error.code === 'VALIDATION_ERROR') {
        throw error;
      }
      throw new ApiError('Failed to create GL account', 'CREATE_ERROR', error);
    }
  }

  /**
   * Update an existing GL account
   */
  async update(id: string, updateData: GLAccountUpdateData): Promise<GLAccount> {
    if (!id) {
      throw new ApiError('GL account ID is required', 'VALIDATION_ERROR');
    }

    try {
      const updatedAccount = await api.glAccount.update(id, updateData);
      
      // Update cache optimistically
      const cached = this.getCachedAccounts();
      if (cached) {
        const updatedCache = cached.map(account => 
          account.id === id ? updatedAccount : account
        );
        this.cacheAccounts(updatedCache);
      }
      
      return updatedAccount;
    } catch (error) {
      throw new ApiError('Failed to update GL account', 'UPDATE_ERROR', error);
    }
  }

  /**
   * Delete a GL account with automatic retry for stale data
   */
  async delete(id: string): Promise<void> {
    if (!id) {
      throw new ApiError('GL account ID is required', 'VALIDATION_ERROR');
    }

    try {
      await api.glAccount.delete(id);
      
      // Update cache optimistically
      const cached = this.getCachedAccounts();
      if (cached) {
        const filteredCache = cached.filter(account => account.id !== id);
        this.cacheAccounts(filteredCache);
      }
    } catch (error: any) {
      // Check if this might be a stale data error
      if (error?.code === 'NOT_FOUND' || 
          error?.message?.includes('not found') || 
          error?.message?.includes('does not exist')) {
        console.warn(`Account ${id} not found on backend, refreshing cache...`);
        await this.forceRefresh();
        throw new ApiError('Account no longer exists. Data has been refreshed, please try again.', 'STALE_DATA', error);
      }
      
      throw new ApiError('Failed to delete GL account', 'DELETE_ERROR', error);
    }
  }

  /**
   * Bulk delete GL accounts
   */
  async deleteBulk(ids: string[]): Promise<void> {
    if (!ids.length) {
      throw new ApiError('No GL account IDs provided', 'VALIDATION_ERROR');
    }

    try {
      await api.glAccount.deleteBulk(ids);
      
      // Update cache optimistically
      const cached = this.getCachedAccounts();
      if (cached) {
        const idSet = new Set(ids);
        const filteredCache = cached.filter(account => !idSet.has(account.id));
        this.cacheAccounts(filteredCache);
      }
    } catch (error) {
      throw new ApiError('Failed to delete GL accounts', 'BULK_DELETE_ERROR', error);
    }
  }

  /**
   * Create multiple GL accounts at once
   */
  async createBulk(accountsData: GLAccountCreateData[]): Promise<any> {
    if (!accountsData.length) {
      throw new ApiError('No GL account data provided', 'VALIDATION_ERROR');
    }

    // Validate all accounts
    for (const account of accountsData) {
      if (!account.number?.trim() || !account.name?.trim()) {
        throw new ApiError('All GL accounts must have number and name', 'VALIDATION_ERROR');
      }
    }

    try {
      const result = await api.glAccount.createBulk(accountsData);
      
      // Force refresh after bulk operations to ensure cache consistency
      if (result.successful && result.successful.length > 0) {
        console.log(`Bulk create completed: ${result.successful.length} accounts created. Refreshing cache...`);
        await this.forceRefresh();
      }
      
      return result;
    } catch (error) {
      throw new ApiError('Failed to create GL accounts', 'BULK_CREATE_ERROR', error);
    }
  }

  /**
   * Search GL accounts by term
   */
  async search(searchTerm: string): Promise<GLAccount[]> {
    const accounts = await this.getAll();
    
    if (!searchTerm.trim()) {
      return accounts;
    }
    
    const term = searchTerm.toLowerCase();
    return accounts.filter(account => 
      account.number.toLowerCase().includes(term) ||
      account.name.toLowerCase().includes(term) ||
      account.description?.toLowerCase().includes(term) ||
      account.category?.toLowerCase().includes(term) ||
      account.subcategory?.toLowerCase().includes(term)
    );
  }

  /**
   * Get GL account by number
   */
  async getByNumber(number: string): Promise<GLAccount | null> {
    const accounts = await this.getAll();
    return accounts.find(account => account.number === number) || null;
  }

  /**
   * Check if GL account number already exists
   */
  async numberExists(number: string, excludeId?: string): Promise<boolean> {
    const accounts = await this.getAll();
    return accounts.some(account => 
      account.number === number && account.id !== excludeId
    );
  }

  /**
   * Refresh cache by forcing a new fetch
   */
  async refresh(): Promise<GLAccount[]> {
    this.clearCache();
    return this.getAll(false);
  }

  /**
   * Force refresh cache (alias for refresh for clarity)
   */
  async forceRefresh(): Promise<GLAccount[]> {
    return this.refresh();
  }

  /**
   * Validate cache against backend and detect inconsistencies
   */
  async validateCache(): Promise<{ isValid: boolean; issues: string[] }> {
    const issues: string[] = [];
    
    try {
      const cached = this.getCachedAccounts();
      const fresh = await api.glAccount.getAll();
      
      if (!cached) {
        return { isValid: true, issues: [] }; // No cache to validate
      }
      
      // Check count mismatch
      if (cached.length !== fresh.length) {
        issues.push(`Account count mismatch: cache has ${cached.length}, backend has ${fresh.length}`);
      }
      
      // Check for accounts in cache but not in backend (phantom accounts)
      const cachedIds = new Set(cached.map(acc => acc.id));
      const freshIds = new Set(fresh.map(acc => acc.id));
      
      const phantomAccounts = cached.filter(acc => !freshIds.has(acc.id));
      if (phantomAccounts.length > 0) {
        issues.push(`${phantomAccounts.length} phantom accounts found in cache`);
      }
      
      // Check for accounts in backend but not in cache (missing accounts)
      const missingAccounts = fresh.filter(acc => !cachedIds.has(acc.id));
      if (missingAccounts.length > 0) {
        issues.push(`${missingAccounts.length} accounts missing from cache`);
      }
      
      return {
        isValid: issues.length === 0,
        issues
      };
    } catch (error) {
      console.error('Cache validation failed:', error);
      return {
        isValid: false,
        issues: ['Cache validation failed due to network error']
      };
    }
  }

  /**
   * Auto-refresh cache if validation detects issues
   */
  async validateAndRefresh(): Promise<GLAccount[]> {
    const validation = await this.validateCache();
    
    if (!validation.isValid) {
      console.warn('Cache validation failed:', validation.issues);
      console.log('Auto-refreshing cache...');
      return this.forceRefresh();
    }
    
    return this.getAll();
  }
}

// Export singleton instance
export const glAccountService = new GLAccountService();
export default glAccountService;